/* global gapi, YT */
/**
 * Google Api max results per page
 * YT (pl, pl items, search results) - 50
 * Google Drive - 1000
 */
import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import appendScriptTag from '../../utilities/appendScriptTag';
import YouTubePlaylistItemWithDuration from '../../models/gapi/gapiPlaylistItemExtensions';
import UploadResponse from '../../models/gapi/uploadResponse';
import mimeTypes from '../../models/mimeTypes';
import QueryIterator from '../../models/queryIterator';
import { StorageProviderKey } from '../../models/storageProviderKeys';
import { Keys, messages, Tasks } from '../../settings';
import compress, { decompress } from '../../utilities/flateCompressDecompress';
import { MediaUploader } from '../../utilities/gapiUpload';
import IStorageProvider from '../storage/istorageProvider';
import { Context } from '../_globalContext/context';
import * as googleAuth from './googleAuth';
import GapiTokenService from './gapiTokenService';
import handleGapiError from './handleGapiError';
import { googleApiKey as apiKey } from '../../config';

/** How long to let gapi client init run before telling the user it is stuck. */
const gapiClientInitTimeoutMs = 20000;

export enum GoogleScope {
  YouTube = 'https://www.googleapis.com/auth/youtube.readonly',
  /**
   * Manage the user's YouTube account - needed to add to and remove from
   * their playlists, which is what two-way playlist sync does. Sensitive tier,
   * like youtube.readonly, so it must be added to the consent screen and
   * re-verified; it is not a restricted scope and does not trigger CASA.
   * Asked for only when a sync that writes to YouTube is set up.
   */
  YouTubeManage = 'https://www.googleapis.com/auth/youtube',
  DriveReadonly = 'https://www.googleapis.com/auth/drive.readonly',
  AppData = 'https://www.googleapis.com/auth/drive.appdata',
}
export enum GoogleMimeTypes {
  File = 'application/vnd.google-apps.file',
  Folder = 'application/vnd.google-apps.folder',
}
export interface PlayVideoOptions {
  /**
   * Whether the player should start on its own once it is ready. Default
   * true; false is what a session restored from localStorage asks for, so
   * that the video is loaded and waiting rather than playing at a user who
   * only opened the app.
   */
  autoplay?: boolean;
}
export interface IGoogleApiContext {
  iframeApiLoaded: boolean;
  dataApiLoaded: boolean;
  gapiClientLoaded: boolean;
  gapiAuthorized: boolean;
  authorizedScopes: string;

  playerEl: React.RefObject<HTMLIFrameElement | null>;
  ytPlayer: React.MutableRefObject<YT.Player | undefined>;

  searchPaged(
    q: any,
    maxResults?: number,
  ): Promise<QueryIterator<gapi.client.youtube.SearchResult>>;
  getUserPlaylistsPaged(maxResults?: number): Promise<QueryIterator<gapi.client.youtube.Playlist>>;
  getPlaylistItemsPaged(
    playlistId: string,
    maxResults?: number,
  ): Promise<QueryIterator<YouTubePlaylistItemWithDuration>>;
  getUserDrive(
    folder: string,
    resultsPerPage: number,
  ): Promise<QueryIterator<gapi.client.drive.File>>;
  getDriveFolder(fileId: string): gapi.client.Request<gapi.client.drive.File>;
  /**
   * One file's name, size and mime type. What a tag refetch asks for, since
   * a library record holds the id alone and the name is where the format
   * comes from.
   */
  getDriveFileMetadata(fileId: string): Promise<gapi.client.drive.File>;
  getFilesInFolderRecursive(folder: string, maxResults?: number): Promise<gapi.client.drive.File[]>;
  getDriveFileUrl(fileId: string, alt?: string, overrideChecks?: boolean): Promise<string>;
  getPlaylist(playlistId: string): Promise<gapi.client.youtube.PlaylistItemListResponse>;
  getDuration(
    ...videoIds: string[]
  ): Promise<gapi.client.Response<gapi.client.youtube.VideoListResponse>>;

  /** Appends a video to one of the user's YouTube playlists. Needs YouTubeManage. */
  insertPlaylistItem(
    playlistId: string,
    videoId: string,
  ): Promise<gapi.client.youtube.PlaylistItem>;
  /**
   * Removes one entry from a YouTube playlist. Takes the playlist *item* id
   * (a video listed twice has two), not the video id. Needs YouTubeManage.
   */
  deletePlaylistItem(playlistItemId: string): Promise<void>;
  /** Whether the current token may change the user's YouTube playlists. */
  canWriteYouTube: boolean;

  resetAppDataFolder(): Promise<void>;
  downloadAppDataFolder(): Promise<void>;

  playVideo(
    callback?: () => void,
    onStateChange?: (ev: YT.OnStateChangeEvent) => void,
    onError?: (ev: YT.OnErrorEvent) => void,
    options?: PlayVideoOptions,
  ): void;

  /**
   * Never settles: it navigates the page to Google, on every platform. Only
   * callers passing `allowRedirect` are allowed to do that - a sign-in button
   * - and work that must follow sign-in belongs in `intent`. Without the
   * flag it rejects, saying where the button is.
   */
  authorize(options?: {
    scope?: string;
    allowRedirect?: boolean;
    intent?: googleAuth.RedirectIntent;
  }): Promise<void>;
  authorizeAdditionalScope(scope: string): Promise<unknown>;
  /** An interactive sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  /**
   * Whether the current token can read the user's own Drive files.
   *
   * `drive.appdata` is not enough and is easy to mistake for enough: it grants
   * the hidden application folder where the library and playlists live, and
   * nothing else. A token holding only that reads settings perfectly well and
   * is refused on every music file.
   */
  canReadDriveFiles: boolean;
  /**
   * Whether interactive authorisation can happen without leaving the page.
   *
   * Always false for Google: signing in is a full-page redirect - which is
   * fine from a button, and not fine from an effect the user did not
   * knowingly trigger, because it tears the app down. Kept on the context
   * because every provider has it and the call sites ask before deciding
   * between "ask now" and "say where the button is". Renewal needs no
   * interaction at all any more, so the question comes up far less.
   */
  canAuthorizeInPlace: boolean;
  logout(): void;
  /**
   * Google rejected the token with a 401. Renews it through the refresh
   * token; only if that fails is the session over - and even then without
   * spending the user's consent, which is what separates it from logout.
   */
  authorizationExpired(): void;
  disconnect(): void;
  /** The current Google token, or undefined when not authorised. */
  getAccount(): googleAuth.GoogleToken | undefined;
  /**
   * Whose Google account this is - the email, with the display name where
   * Drive gives one. Undefined until it has been fetched, and while not
   * authorised.
   */
  googleUser: { name?: string; email?: string } | undefined;
}
export const GoogleApiContext = React.createContext({} as IGoogleApiContext);

interface Props {
  children: React.ReactNode;
}

/**
 * The access token a request made right now should carry.
 *
 * Renews first if the token in hand has lapsed - see googleAuth.ensureToken
 * for why reading the token directly was wrong - and translates a renewal
 * that could not happen into the message every caller here was already
 * throwing for a missing token.
 */
function freshAccessToken(): Promise<string> {
  return googleAuth.ensureToken().then(
    (token) => token.accessToken,
    (err: unknown) => {
      // Not signed in, or Google has ended the session: the setup screen is
      // the answer, and the standard message says so. Anything else is the
      // backend or the network, which says why - and that is the one fact
      // the user can act on. "Sign in again" there would be wrong twice
      // over: it reads as though the app had forgotten them, and signing in
      // would not help.
      if (
        err instanceof googleAuth.GoogleAuthError &&
        err.type !== 'not_signed_in' &&
        err.type !== 'invalid_grant'
      ) {
        throw `Google access could not be renewed: ${err.message}`;
      }

      throw messages(Keys.storageProviderNotLoggedIn);
    },
  );
}

export default function GapiService({ children }: Props) {
  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  /**
   * Caches the drive ids of app data files
   * Behaves like a ref
   *
   * useState is quicker over useRef, erronously all methods below request this obj
   * as dependency which is unneeded.
   */
  const [appDataIdsCache] = useState({} as any);
  const sp = useRef<IStorageProvider>(undefined);
  const getAppDataIdsPromise = useRef<Promise<gapi.client.drive.File[]>>(undefined);
  const [gapiTokenService] = useState(() => new GapiTokenService());

  /**
   * Root element for the youtube iframe player
   */
  const playerEl = useRef<HTMLIFrameElement>(null);
  const ytPlayer = useRef<YT.Player>(undefined);

  const [iframeApiLoaded, setIframeApiLoaded] = useState(false);
  const [dataApiLoaded, setDataApiLoaded] = useState(false);
  const [gapiClientLoaded, setGapiClientLoaded] = useState(false);
  /**
   * Currently this flag doesn't say much
   * Since it's possible to use Google Drive w/o using it as the AppData storage provider
   *
   * It is set upon successful authorization,
   * verified by testing for the profile scope
   */
  const [gapiAuthorized, setGapiAuthorized] = useState(false);

  // Never includes base appdata scope, we track gapiAuthorized for that
  const [authorizedScopes, setAuthorizedScopes] = useState<string>('');

  // Read from the token, not from authorizedScopes - that string deliberately
  // omits appdata and so cannot answer this on its own. Every path that
  // changes the token also moves gapiAuthorized or authorizedScopes, so a
  // plain render-time read stays current.
  const canReadDriveFiles = googleAuth.hasScope(GoogleScope.DriveReadonly);
  const canWriteYouTube = googleAuth.hasScope(GoogleScope.YouTubeManage);

  // A constant, but kept alongside the scope flag because call sites need
  // both to decide between "ask now" and "tell the user what to press". See
  // the interface comment.
  const canAuthorizeInPlace = false;

  /**
   * What an interactive sign-in should ask Google for: as little as will do.
   *
   * Scopes are cumulative within a session - Google issues a token for exactly
   * what is asked for, so a request must carry the scopes the live token
   * already holds or they are dropped from it.
   *
   * What it deliberately does *not* carry is `rememberedScopes()`, the union
   * of everything this browser has ever been granted. Replaying that at
   * sign-in is how pressing the setup screen's one button asked for Drive and
   * YouTube as well: months-old permissions, re-requested by a user who only
   * wanted to sign in. Worse, a set wider than what Google currently holds
   * consent for turns a would-be silent renewal into a consent screen.
   *
   * Nothing is lost by asking narrowly. Google adds previously granted scopes
   * back on its own (googleAuth sends `include_granted_scopes=true`), and
   * where it does not, every page that needs more asks for its own scope
   * behind its own button - the Drive browser for drive.readonly, the
   * YouTube page for youtube.readonly. Which is where the asking belongs.
   */
  const scopesToRequest = useCallback(function (scope?: string) {
    const held = googleAuth.grantedScopes().split(' ').filter(Boolean);

    if (scope && held.indexOf(scope) === -1) {
      held.push(scope);
    }

    // The library itself lives in the appdata folder, so a token that cannot
    // reach it is no use while Drive is the storage provider. An expired token
    // carries nothing, so this cannot come from `held`.
    const driveIsStorageProvider =
      !!sp.current && ctxRef.current.storageProvider.current === sp.current;

    if ((driveIsStorageProvider || held.length === 0) && held.indexOf(GoogleScope.AppData) === -1) {
      held.push(GoogleScope.AppData);
    }

    return held.join(' ');
  }, []);

  /**
   * Navigates the page to Google and never resolves - the app is torn down
   * mid-promise, on every platform. So do not chain work onto it that has to
   * happen after sign-in; describe that work in `intent` instead, and
   * replayRedirectIntent below runs it when the token comes back.
   *
   * A redirect is destructive in a way a popup was not: queued library and
   * playlist writes have a 5 minute debounce behind them. That is fine when
   * the user has just pressed a sign-in button and is expecting to go
   * somewhere, and not fine from an effect - so without `allowRedirect`
   * this rejects, saying where the button is, rather than leaving.
   */
  const doAuthorize = useCallback(
    async function (options?: {
      scope?: string;
      allowRedirect?: boolean;
      intent?: googleAuth.RedirectIntent;
    }) {
      if (!options?.allowRedirect) {
        throw new Error(messages(Keys.googleSignInNeedsButton));
      }

      await googleAuth.beginSignIn({
        scope: scopesToRequest(options.scope),
        intent: options.intent,
      });
    },
    [scopesToRequest],
  );

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>(undefined);

  /**
   * One sign-in at a time - the same shape oneDriveService and
   * dropboxService have, for the same reason: a second press while the PKCE
   * challenge is still being derived must not start a second trip. The
   * second caller gets the first promise instead, and isAuthorizing lets the
   * button say so.
   *
   * The promise never settles and the flag never clears, deliberately: the
   * page is going away, and "Signing in…" is the truth until it does.
   */
  const authorize = useCallback(
    function (options?: {
      scope?: string;
      allowRedirect?: boolean;
      intent?: googleAuth.RedirectIntent;
    }): Promise<void> {
      if (authorizeInFlight.current) {
        return authorizeInFlight.current;
      }

      setAuthorizing(true);

      authorizeInFlight.current = doAuthorize(options).finally(function settled() {
        authorizeInFlight.current = undefined;
        setAuthorizing(false);
      });

      return authorizeInFlight.current;
    },
    [doAuthorize],
  );

  /**
   * Every live caller is a sign-in button - the Drive browser asking for
   * drive.readonly, the YouTube page asking for youtube.readonly, the
   * playlist sync button asking for youtube - so the redirect is allowed
   * here for the same reason it is on the setup screen.
   *
   * No intent is needed: beginSignIn remembers the current path, so the user
   * lands back on the page whose button they pressed.
   */
  const authorizeAdditionalScope = useCallback(
    async function (scope: GoogleScope) {
      await authorize({ scope, allowRedirect: true });
    },
    [authorize],
  );

  /**
   * Give up the Google session, and put the app back on the setup screen if
   * the library was being read out of Drive.
   *
   * The difference between the two reasons for doing so is the whole point of
   * this function:
   *
   * - 'revoke' tells Google to forget the consent as well. A deliberate
   *   logout, where starting from scratch is what the user asked for.
   * - 'expired' forgets only what this browser holds: the refresh token has
   *   stopped working, or there never was one. Consent stands with Google,
   *   so the next sign-in is a short trip rather than a fresh decision.
   */
  const endGoogleSession = useCallback(function (how: 'revoke' | 'expired') {
    function complete() {
      if (how === 'revoke') {
        googleAuth.revokeSession();
      } else {
        googleAuth.clearSession();
      }

      setGapiAuthorized(false);
      setAuthorizedScopes('');
    }

    if (ctxRef.current.storageProvider.current === sp.current) {
      ctxRef.current.configureStorageProvider();
      ctxRef.current.spLogoutHandling().then(complete);
    } else {
      complete();
    }
  }, []);

  const logout = useCallback(
    function logout() {
      endGoogleSession('revoke');
    },
    [endGoogleSession],
  );

  /**
   * The token stopped being accepted - it lapsed, or Google withdrew it.
   *
   * First answer: renew it. A 401 on a token this browser thought was live
   * is most often the clock - a tab asleep past the renewal, a laptop lid -
   * and the refresh token fixes that without anyone noticing. Only when the
   * renewal itself fails is the session over, and even then it must not
   * revoke: revoking on 401 once spent the user's consent every time a token
   * aged out, and the next sign-in was the full consent screen for
   * permissions that had never been taken away.
   */
  const authorizationExpired = useCallback(
    function () {
      googleAuth.renewAfterRejection().catch(() => {
        ctxRef.current.callSnackbar(messages(Keys.storageProviderNotLoggedIn));
        endGoogleSession('expired');
      });
    },
    [endGoogleSession],
  );

  /**
   * Revoking the token withdraws the app's access outright, the same as
   * removing it from https://myaccount.google.com/permissions
   *
   * Under GIS this is exactly what the Logout button already does - there is
   * no session left over to sign out of separately. Kept as its own name
   * because "revoke" is what the code above calls it.
   */
  const disconnect = useCallback(
    function disconnect() {
      endGoogleSession('revoke');
    },
    [endGoogleSession],
  );

  /**
   * GIS issues access tokens and nothing else - there is no profile attached
   * to one, so the token says nothing about whose account it is.
   */
  const getAccount = useCallback(function () {
    return googleAuth.getToken();
  }, []);

  const [googleUser, setGoogleUser] = useState<{ name?: string; email?: string }>();

  /**
   * Whose account it is, from Drive rather than from the token.
   *
   * There is no profile on an access token, and the userinfo endpoint
   * needs a scope Fadila does not ask for. `about.get` does not: its
   * reference says "Requires one of the following OAuth scopes" and lists
   * **drive.appdata** among the seven, and appdata is the scope the library
   * itself is stored under - so every signed-in user has already granted it.
   * The signed-in email therefore costs one request and no new consent.
   *
   * **This is the documented contract, not a measured result.** The common
   * case is appdata *alone*: the easy sign-in asks for that and nothing else,
   * and drive.readonly is only added when the user first browses Drive. If
   * Google turns out to want a broader scope for the `user` field, the effect
   * below fails, this stays undefined, and the row shows no name - which is
   * where it was before any of this. Nothing else depends on it.
   *
   * So: failure is not an error worth a snackbar, but it is worth a line in
   * the console, because a name that never appears is otherwise invisible.
   * If that line ever shows a 403, the fallback to try is `owners` on an
   * appdata file, which is squarely inside the appdata scope.
   */
  useEffect(
    function fetchGoogleUser() {
      if (!gapiClientLoaded || !gapiAuthorized) {
        setGoogleUser(undefined);
        return;
      }

      let current = true;

      gapi.client.drive.about
        .get({ fields: 'user' })
        .then((res) => {
          const user = res.result.user;

          if (current && user) {
            setGoogleUser({
              name: user.displayName || undefined,
              email: user.emailAddress || undefined,
            });
          }
        })
        .catch((err: any) => {
          // An under-scoped or expired token, or Drive being unreachable.
          console.debug(
            'Google account name unavailable (about.get):',
            err?.result?.error?.message || err?.status || err,
          );
        });

      return () => {
        current = false;
      };
    },
    [gapiClientLoaded, gapiAuthorized],
  );

  const errorHandler = useCallback(
    handleGapiError(ctxRef.current.callSnackbar, authorizationExpired),
    [authorizationExpired],
  );

  useEffect(function registerAsStorageProvider() {
    ctxRef.current.storageProviderMap[StorageProviderKey.GoogleDrive] = sp;
  }, []);

  /**
   * Authorisation state follows the token, rather than only being set at the
   * moment we ask for one.
   *
   * Tokens arrive from the redirect route, from the hourly renewal, from a
   * renewal another tab did, and go away when a refresh token stops working
   * or a logout happens elsewhere. None of those pass through this component,
   * so without this the UI would go on claiming whatever it last decided.
   */
  useEffect(function trackTokenState() {
    return googleAuth.onTokenChange((token) => {
      setGapiAuthorized(!!token);
      setAuthorizedScopes(token?.scope ?? '');
    });
  }, []);

  useEffect(function loadScripts() {
    appendScriptTag(
      'https://www.youtube.com/iframe_api',
      undefined,
      () => setIframeApiLoaded(true),
      (event: Event | string, _source?: string, _lineno?: number, _colno?: number, error?: Error) =>
        ctxRef.current.callSnackbar(error || event),
    );
    appendScriptTag(
      'https://apis.google.com/js/api.js',
      undefined,
      () => setDataApiLoaded(true),
      (event: Event | string, _source?: string, _lineno?: number, _colno?: number, error?: Error) =>
        ctxRef.current.callSnackbar(error || event),
    );

    // Subscribe before anything can publish a token, so gapi.client is never
    // the last to know.
    gapiTokenService.start();

    // A sign-in comes back as a reload of the page, so a token obtained
    // seconds ago arrives as storage rather than as a callback. Pick it up
    // before anything concludes the user is signed out. A token that has
    // lapsed since the last visit is renewed through the backend in the
    // background - no window, no gesture - and trackTokenState above hears
    // about it when it lands.
    const restored = googleAuth.restoreSession();

    if (restored) {
      setGapiAuthorized(true);
      setAuthorizedScopes(restored.scope);
    }

    // The redirect intent is deliberately NOT replayed here. sp.current is
    // assigned by updateStorageProvider, which is declared far below this and
    // therefore runs after it on mount - so configureStorageProvider called
    // from here always found nothing registered and said so. See
    // replayRedirectIntent, which is placed after that effect for this reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Determines if the external google libraries have been loaded.
   * When loaded we can proceed to initializing the google api client.
   */
  useEffect(
    function shouldInitClient() {
      /**
       * Set once initialisation reaches any conclusion, success or failure.
       * The watchdog below uses it to tell "still working" from "wedged".
       */
      let settled = false;

      /**
       * One retry before giving up.
       *
       * gapi.client.load fetches discovery documents over the network and offers
       * no timeout of its own, so a request that dies in flight leaves it waiting
       * forever - and with it the whole loading checklist, since gapiClientLoaded
       * never flips. iOS kills in-flight requests routinely: any moment the page
       * spends backgrounded during startup will do it. Warning about that and
       * then doing nothing left the app hung on a screen the user cannot get past.
       */
      let retried = false;
      let watchdog: ReturnType<typeof setTimeout>;

      function retryOrWarnClientInit() {
        if (settled) {
          return;
        }

        if (!retried) {
          retried = true;
          watchdog = setTimeout(retryOrWarnClientInit, gapiClientInitTimeoutMs);

          try {
            initClient();
          } catch (err) {
            settled = true;
            errorHandler(err);
          }

          return;
        }

        ctxRef.current.callSnackbar(
          'Google client API has not finished initialising. ' +
            'The discovery documents for Drive and YouTube could not be loaded.',
        );
      }

      watchdog = setTimeout(retryOrWarnClientInit, gapiClientInitTimeoutMs);

      async function initClient() {
        /**
         * Initialize the google api client with correct api key and YouTube data api v3
         */
        gapi.load('client', async () => {
          // Everything in here must be caught locally. This callback is async and
          // is invoked by gapi, so a rejection escaping it is an unhandled
          // rejection nobody sees - and since gapiClientLoaded then never flips,
          // the app sits on the loading screen forever with no error at all.
          try {
            gapi.client.setApiKey(apiKey);
            await gapi.client.load('youtube', 'v3');
            await gapi.client.load('drive', 'v3');

            // Authorisation is deliberately NOT part of this gate any more.
            //
            // gapi.auth2.init() used to run here, and startup blocked until it
            // resolved - so a browser that would not let it confirm a session
            // (any Safari, thanks to ITP) hung the whole app on the loading
            // screen, even for people whose library lives in OneDrive or
            // Dropbox and who never needed Google at all.
            //
            // GIS has no init step and no session to restore. The client is
            // ready once the discovery documents are in; a token is requested
            // later, from a user gesture, and only when Google is actually used.

            // The silent token attempt races this, and gapi.client cannot be
            // given a token before it exists. If that race was won by the token,
            // hand it over now - otherwise gapi would make unauthenticated calls
            // until the next token change.
            const existingToken = googleAuth.getAccessToken();

            if (existingToken) {
              gapi.client.setToken({ access_token: existingToken });
            }

            settled = true;
            setGapiClientLoaded(true);
          } catch (err) {
            settled = true;
            errorHandler(err);
          }
        });
      }

      if (dataApiLoaded) {
        try {
          initClient();
        } catch (err) {
          settled = true;
          errorHandler(err);
        }
      } else {
        // Nothing started, so nothing to warn about yet.
        settled = true;
      }

      return function cancelWatchdog() {
        clearTimeout(watchdog);
      };
    },
    [dataApiLoaded],
  );

  const searchPaged = useCallback(
    function (q: any, maxResults?: number) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }

      let pageToken: string | undefined;

      function doSearch(q: string, _maxResults?: number, pageToken?: string) {
        return gapi.client.youtube.search.list({
          q,
          part: 'snippet',
          fields:
            'items(id,snippet(thumbnails/default,title,channelTitle)),nextPageToken,pageInfo/totalResults',
          maxResults: 50,
          pageToken,
        });
      }

      return (function nextPagedResultsClosure() {
        return (async function nextPagedResults(
          maxResults?: number,
        ): Promise<QueryIterator<gapi.client.youtube.SearchResult>> {
          const res = await doSearch(q, maxResults, pageToken);

          pageToken = res.result.nextPageToken;

          // The returned object holds the result of the first execution and
          // allows for further execution
          return {
            results: res.result.items || [],
            totalResults: res.result.pageInfo!.totalResults || 0,
            next: res.result.nextPageToken ? nextPagedResults : undefined,
          };
        })(maxResults);
      })();
    },
    [gapiClientLoaded],
  );

  /**
   *
   * @param maxResults
   * Update: Fetching less than the displayed per page creates
   * a silly UX where you press next page but it just lenghtens the list
   * so disregard the following..
   *
   * We fetch greedily despite only displaying a portion of returned results
   * Therefore we usually leave this option undefined and leave the api to decide the result count
   */
  const getUserPlaylistsPaged = useCallback(
    function (maxResults?: number) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }
      if (!gapiAuthorized) {
        ctxRef.current.callSnackbar('User not authorized for request');
        return Promise.reject();
      }

      let pageToken: string | undefined;

      function gapiGetPlaylists(_maxResults?: number, pageToken?: string) {
        return gapi.client.youtube.playlists.list({
          part: 'snippet,contentDetails',
          maxResults: 50,
          fields:
            'items(id,snippet(thumbnails/default,title),contentDetails/itemCount),nextPageToken,pageInfo/totalResults',
          mine: true,
          pageToken,
        });
      }

      return (function nextPagedResultsClosure() {
        return (async function nextPagedResults(
          maxResults?: number,
        ): Promise<QueryIterator<gapi.client.youtube.Playlist>> {
          const res = await gapiGetPlaylists(maxResults, pageToken);

          pageToken = res.result.nextPageToken;

          // The returned object holds the result of the first execution and
          // allows for further execution
          return {
            results: res.result.items || [],
            totalResults: res.result.pageInfo!.totalResults || 0,
            next: res.result.nextPageToken ? nextPagedResults : undefined,
          };
        })(maxResults);
      })();
    },
    [gapiClientLoaded, gapiAuthorized],
  );

  const getPlaylist = useCallback(
    async function (playlistId: string, _maxResults?: number, pageToken?: string) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }

      const res = await gapi.client.youtube.playlistItems.list({
        part: 'snippet,contentDetails',
        maxResults: 50,
        playlistId,
        // `id` is the playlist *item* id, which playlistItems.delete wants and
        // which the sync uses to remove a video from a YouTube playlist.
        fields:
          'items(id,contentDetails/videoId,snippet(description,thumbnails/default,title,publishedAt,videoOwnerChannelTitle)),nextPageToken,pageInfo/totalResults',
        pageToken,
      });

      return res.result;
    },
    [gapiClientLoaded],
  );

  /**
   * Both write calls cost 50 quota units each against a default daily quota
   * of 10,000, so a sync that moves a couple of hundred videos in a day is
   * the ceiling. Reads are 1 unit a page; the sync is not the expensive part.
   */
  const insertPlaylistItem = useCallback(
    async function (playlistId: string, videoId: string) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }
      if (!googleAuth.hasScope(GoogleScope.YouTubeManage)) {
        return Promise.reject(messages(Keys.youTubeWriteAccessMissing));
      }

      const res = await gapi.client.youtube.playlistItems.insert({
        part: 'snippet',
        resource: {
          snippet: {
            playlistId,
            resourceId: {
              kind: 'youtube#video',
              videoId,
            },
          },
        },
      });

      return res.result;
    },
    [gapiClientLoaded],
  );

  const deletePlaylistItem = useCallback(
    async function (playlistItemId: string) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }
      if (!googleAuth.hasScope(GoogleScope.YouTubeManage)) {
        return Promise.reject(messages(Keys.youTubeWriteAccessMissing));
      }

      await gapi.client.youtube.playlistItems.delete({
        id: playlistItemId,
      });
    },
    [gapiClientLoaded],
  );
  const getDuration = useCallback(async function (...videoIds: string[]) {
    return await gapi.client.youtube.videos.list({
      part: 'contentDetails',
      fields: 'items(id,contentDetails/duration)',
      id: videoIds.join(','),
      maxResults: 50,
    });
  }, []);

  /**
   *
   * @param maxResults
   */
  const getPlaylistItemsPaged = useCallback(
    function (playlistId: string, maxResults?: number) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }

      let pageToken: string | undefined;

      return (function nextPagedResultsClosure() {
        return (async function nextPagedResults(
          maxResults?: number,
        ): Promise<QueryIterator<YouTubePlaylistItemWithDuration>> {
          const res = await getPlaylist(playlistId, maxResults, pageToken);

          // update items with duration
          //
          // Length check, not just presence: an empty playlist answers with
          // `items: []`, and videos.list with no ids is a 400. Empty playlists
          // used to be rare here (why open one?) and are now routine - a
          // playlist made on YouTube to receive a Fadila playlist starts empty.
          if (res.items && res.items.length) {
            const durations = await getDuration(
              ...res.items.map((itm) => itm.contentDetails!.videoId!),
            );

            if (durations.result.nextPageToken) {
              Sentry.captureMessage(
                'found nextPageToken in durations.result. Indicates that we have to refactor getDuration in getPlaylistItemsPaged. We were assuming getDuration had same max results.',
                Severity.Error,
              );
            }

            res.items = res.items.map((itm) => {
              const matchingDuration = durations.result.items?.find(
                (durItm) => durItm.id === itm.contentDetails!.videoId!,
              );

              if (matchingDuration?.contentDetails?.duration) {
                (itm as YouTubePlaylistItemWithDuration).duration =
                  matchingDuration?.contentDetails?.duration;
              }

              return itm;
            });
          }

          pageToken = res.nextPageToken;

          // The returned object holds the result of the first execution and
          // allows for further execution
          return {
            results: res.items || [],
            totalResults: res.pageInfo!.totalResults || 0,
            next: res.nextPageToken ? nextPagedResults : undefined,
          };
        })(maxResults);
      })();
    },
    [gapiClientLoaded, getPlaylist, getDuration],
  );

  const gapiGetFiles = useCallback(function (
    folder: string,
    maxResults?: number,
    pageToken?: string,
  ) {
    return gapi.client.drive.files.list({
      q: `'${folder}' in parents and trashed = false`,
      pageSize: Math.min(1000, Math.max(100, maxResults || 1)),
      fields: 'files(iconLink,id,mimeType,name,size),nextPageToken',
      pageToken,
    });
  }, []);

  /**
   * Get GoogleDrive folder files
   * @param folder
   * @param maxResults
   */
  const getUserDrive = useCallback(
    async function (folder: string, maxResults?: number) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }
      if (!gapiAuthorized) {
        ctxRef.current.callSnackbar('User not authorized for request');
        return Promise.reject();
      }

      let pageToken: string | undefined;

      return (function nextPagedResultsClosure() {
        return (async function nextPagedResults(
          maxResults?: number,
        ): Promise<QueryIterator<gapi.client.drive.File>> {
          const res = await gapiGetFiles(folder, maxResults, pageToken);

          pageToken = res.result.nextPageToken;

          // The returned object holds the result of the first execution and
          // allows for further execution
          return {
            results: res.result.files || [],
            next: res.result.nextPageToken ? nextPagedResults : undefined,
          };
        })(maxResults);
      })();
    },
    [gapiClientLoaded, gapiAuthorized, gapiGetFiles],
  );

  const getFilesInFolderRecursive = useCallback(
    async function (folder: string) {
      const maxResults = 1000;
      const files: gapi.client.drive.File[] = [];
      const allFolders: gapi.client.drive.File[] = [];
      let pageToken: string | undefined;

      do {
        var iter = await gapiGetFiles(folder, maxResults, pageToken);

        pageToken = iter.result.nextPageToken;
        if (iter.result.files) {
          const folders = iter.result.files.filter(
            (x) => x.mimeType === 'application/vnd.google-apps.folder',
          );
          allFolders.push(...folders);
          const audioItems = iter.result.files.filter((x) => mimeTypes.has(x.mimeType!));
          files.push(...audioItems);
        }
      } while (iter.result.nextPageToken);

      for (const folder of allFolders) {
        const fil = await getFilesInFolderRecursive(folder.id!);
        files.push(...fil);
      }

      return files;
    },
    [gapiGetFiles],
  );

  const getDriveFolder = useCallback(
    function (fileId: string) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        throw new Error('Unable to access google api, client not ready');
      }
      if (!gapiAuthorized) {
        ctxRef.current.callSnackbar('User not authorized for request');
        throw new Error('User not authorized for request');
      }

      return gapi.client.drive.files.get({
        fileId,
        fields: 'parents,name',
      });
    },
    [gapiClientLoaded, gapiAuthorized],
  );

  const getDriveFileMetadata = useCallback(
    async function (fileId: string) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        throw new Error('Unable to access google api, client not ready');
      }
      if (!gapiAuthorized) {
        ctxRef.current.callSnackbar('User not authorized for request');
        throw new Error('User not authorized for request');
      }

      const res = await gapi.client.drive.files.get({
        fileId,
        fields: 'id,name,size,mimeType',
      });

      return res.result;
    },
    [gapiClientLoaded, gapiAuthorized],
  );

  /**
   * Gets the drive file url set Html Audio element src to
   *
   * @param fileId Google Drive unique file id
   * @param alt Used to control download, see Google APi
   * @param overrideChecks When player triggers authorization we don't want to wait for state to update before calling getDriveFileUrl
   */
  const getDriveFileUrl = useCallback(
    async function (fileId: string, alt = 'media', overrideChecks = false) {
      // Checked even when overrideChecks is set: that flag exists to skip the
      // *authorization state* checks during the player's auth flow, not this.
      //
      // The URL returned below carries no credentials at all. The service worker
      // is the only thing that turns it into an authenticated request, and the
      // only consumer that needs that is <audio> playback, which cannot set
      // headers itself. Without the worker every play is a 401, so fail loudly
      // here rather than letting the player sit and spin.
      //
      // The appdata reads in getOrCreateDataFile used to come through here as
      // well, which made the entire startup depend on the worker. They set
      // their own Authorization now and never touch this function.
      if (!ctxRef.current.deviceCapabilities.hasServiceWorkers) {
        ctxRef.current.callSnackbar(messages(Keys.driveRequiresServiceWorker));
        return Promise.reject(messages(Keys.driveRequiresServiceWorker));
      }

      // The URL carries no credentials. The service worker attaches the
      // Authorization header on the way out - see thesw.js - because the <audio>
      // element cannot set headers itself. Renewing here, ahead of the scope
      // checks, is what makes those checks answer for the token the worker will
      // actually send rather than for one that lapsed a minute ago.
      try {
        await freshAccessToken();
      } catch (err) {
        ctxRef.current.callSnackbar(err);
        return Promise.reject(err);
      }

      if (!overrideChecks) {
        if (!gapiClientLoaded) {
          ctxRef.current.callSnackbar('Unable to access google api, client not ready');
          return Promise.reject();
        }
        if (
          !googleAuth.hasScope(GoogleScope.AppData) &&
          !googleAuth.hasScope(GoogleScope.DriveReadonly)
        ) {
          ctxRef.current.callSnackbar('User not authorized for request');
          return Promise.reject();
        }
      }

      return `https://www.googleapis.com/drive/v3/files/${fileId}?alt=${alt}`;

      // Scopes are read live from googleAuth rather than captured here, so they
      // are deliberately not dependencies.
    },
    [gapiClientLoaded],
  );

  const putAppDataFile = useCallback(
    async function doPutAppDataFile(name: string, file: any) {
      // Uploads authenticate themselves rather than going through the service
      // worker, so the token has to be good here and now - renewed first if the
      // timer has not got to it yet.
      const accessToken = await freshAccessToken();

      const prevFileId = appDataIdsCache[name];

      if (!(file instanceof Blob)) {
        const compressed = compress(file, ctxRef.current.flate);
        file = new Blob([compressed], { type: 'application/x-gzip' });
      }

      const res: any = await new Promise(function (resolve, reject) {
        const uploader = new MediaUploader({
          file,
          token: accessToken,
          metadata: {
            name,
            parents: prevFileId ? undefined : ['appDataFolder'],
          },
          fileId: prevFileId,
          onComplete: resolve,
          onError: reject,
        });
        uploader.upload();
      });

      const jsonResponse: UploadResponse = JSON.parse(res);
      appDataIdsCache[jsonResponse.name] = jsonResponse.id;
    },
    [appDataIdsCache],
  );

  const getAppDataIds = useCallback(
    async function (resetAppData?: boolean) {
      async function doGetAppDataIds() {
        const files = new Array<gapi.client.drive.File>();
        let pageToken: string | undefined;

        do {
          var res = await gapi.client.drive.files.list({
            spaces: 'appDataFolder',
            fields: 'nextPageToken, files(id, name)',
            pageSize: 1000,
            pageToken,
          });

          pageToken = res.result.nextPageToken;
          if (res.result.files) {
            files.push(...res.result.files);
          }
        } while (res.result.nextPageToken);

        // Sort files by name
        files.sort((a, b) => (a.name! < b.name! ? -1 : a.name === b.name ? 0 : 1));

        // If resetting, don't warn
        if (!resetAppData) {
          let prev;

          // We used to have alot of issues with duplicate app data files in GDrive
          for (const file of files) {
            if (prev && file && prev.name === file.name) {
              ctxRef.current.callSnackbar(messages(Keys.googleAppDataDuplicates));
              break;
            }

            prev = file;
          }
        }

        files.forEach((file) => (appDataIdsCache[file.name!] = file.id));

        return files;
      }

      // One request is enough
      if (!resetAppData && getAppDataIdsPromise.current) {
        try {
          await getAppDataIdsPromise.current;
          return;
        } catch (err) {} // Don't handle, just try again
      }

      getAppDataIdsPromise.current = doGetAppDataIds();
      return await getAppDataIdsPromise.current;
    },
    [appDataIdsCache],
  );

  const getOrCreateDataFile = useCallback(
    async function (fileName: string, defaultData: any) {
      try {
        if (!appDataIdsCache[fileName]) {
          await getAppDataIds();
        }

        if (appDataIdsCache[fileName]) {
          // Authenticate here, not through the service worker. The worker
          // exists for <audio>, which cannot set headers; a fetch can, and
          // the upload path below already does. Routing these three reads
          // through the worker meant the whole startup - settings, library,
          // playlists - died with a 403 whenever the worker was not in the
          // path: a hard reload runs the page uncontrolled, and at least one
          // ad blocker stopped it intercepting altogether. The worker sees a
          // request that already carries Authorization and leaves it be.
          const accessToken = await freshAccessToken();

          const resp = await fetch(
            `https://www.googleapis.com/drive/v3/files/${appDataIdsCache[fileName]}?alt=media`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );

          if (resp.ok) {
            const buffer = await resp.arrayBuffer();
            return decompress(buffer, ctxRef.current.flate);
          } else {
            throw resp;
          }
        } else {
          await putAppDataFile(fileName, defaultData);
          return defaultData;
        }
      } catch (err) {
        errorHandler(err);
        return Promise.reject();
      }
    },
    [errorHandler, putAppDataFile, appDataIdsCache, getAppDataIds],
  );

  const resetAppDataFolder = useCallback(
    async function () {
      ctxRef.current.setRunningTask(Tasks.resetAppDataFolder);

      try {
        const files = await getAppDataIds(true);

        if (files) {
          for (const file of files) {
            await gapi.client.drive.files.delete({
              fileId: file.id!,
            });
          }
        }

        try {
          localStorage.clear();
        } catch {}
      } finally {
        ctxRef.current.removeRunningTask(Tasks.resetAppDataFolder);
      }

      window.location.reload();
    },
    [getAppDataIds],
  );

  const downloadAppDataFolder = useCallback(
    async function () {
      ctxRef.current.setRunningTask(Tasks.downloadAppDataFolder);

      try {
        const files = await getAppDataIds(true);

        if (files) {
          const temporaryDownloadLink = document.createElement('a');
          temporaryDownloadLink.style.display = 'none';

          document.body.appendChild(temporaryDownloadLink);

          for (const file of files) {
            const accessToken = await freshAccessToken();

            const res = await fetch(
              `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`,
              {
                headers: new Headers({
                  Authorization: `Bearer ${accessToken}`,
                }),
              },
            );

            const blob = await res.blob();
            const objUrl = window.URL.createObjectURL(blob);

            temporaryDownloadLink.setAttribute('href', objUrl);
            temporaryDownloadLink.setAttribute('download', file.name!);

            temporaryDownloadLink.click();
          }

          document.body.removeChild(temporaryDownloadLink);
        }
      } finally {
        ctxRef.current.removeRunningTask(Tasks.downloadAppDataFolder);
      }
    },
    [getAppDataIds],
  );

  // saveFile() lived here: it created Music/Podcasts folders and uploaded into
  // them, and was the only caller that ever asked for GoogleScope.Drive - full
  // read/write over the user's entire Drive, the most restricted scope Google
  // publishes. It was dead code (never wired into the storage provider below),
  // and a restricted scope costs an annual third-party security assessment at
  // OAuth verification time, so it is deleted rather than left dormant. If
  // writing to Drive is ever wanted, do it with drive.file and the Picker.

  useEffect(
    function updateStorageProvider() {
      sp.current = {
        name: StorageProviderKey.GoogleDrive,
        load: (fileName: string, defaultData: any) => {
          // Read the token live rather than the React state captured when this
          // effect last ran. configureStorageProvider is called straight after
          // authorize() resolves, but effects are deferred until after paint - so
          // the first load() ran against a closure that still said "not
          // authorised", reset the provider, and bounced the user back to the
          // sign-in screen. Endlessly, once GIS made the token arrive on the
          // click rather than during startup.
          //
          // And renew it first. hasScope() on its own answered false for a
          // token that had merely expired, which took the same bounce - forget
          // the provider, back to setup - for the ordinary event of an hour
          // passing. That reset is kept for what it was written for: a token
          // that genuinely does not carry the appdata grant, and no way to get
          // one quietly.
          return googleAuth.ensureToken().then(
            (token) => {
              if (token.scope.split(' ').indexOf(GoogleScope.AppData) === -1) {
                ctxRef.current.configureStorageProvider();
                throw messages(Keys.storageProviderNotLoggedIn);
              }

              return getOrCreateDataFile(fileName, defaultData);
            },
            () => {
              // Nothing to read with and no quiet way to get it: the session is
              // over, and the setup screen is where it starts again.
              ctxRef.current.configureStorageProvider();
              throw messages(Keys.storageProviderNotLoggedIn);
            },
          );
        },
        // save: not supported. The implementation it used to call (saveFile) is
        // gone along with the full-drive scope it needed - see the note above.
        saveAppData: (fileName: string, fileData: any) => {
          // Uploads go through gapi.client, which authenticates itself, so this
          // would otherwise succeed without the worker - and that is the danger.
          // If the read failed we are holding default/empty state, and writing
          // that back would destroy the user's real file.
          if (!ctxRef.current.deviceCapabilities.hasServiceWorkers) {
            return Promise.reject(messages(Keys.driveRequiresServiceWorker));
          }
          // Read the token directly rather than the React state captured when this
          // effect last ran. configureStorageProvider is called straight after
          // authorize() resolves, but effects are deferred until after paint - so
          // the first load() ran against a closure that still said "not
          // authorised", reset the provider, and bounced the user back to the
          // sign-in screen. Endlessly, once GIS made the token arrive on the
          // click rather than during startup.
          //
          // A write, unlike a load, never resets the provider over a renewal
          // that failed: the data it carries is the user's edit, the queue that
          // called keeps it and tries again, and throwing the app back to the
          // setup screen in the meantime would lose that edit for certain.
          return googleAuth.ensureToken().then(
            (token) => {
              if (token.scope.split(' ').indexOf(GoogleScope.AppData) === -1) {
                ctxRef.current.configureStorageProvider();
                throw messages(Keys.storageProviderNotLoggedIn);
              }

              return putAppDataFile(fileName, fileData);
            },
            () => {
              throw messages(Keys.storageProviderNotLoggedIn);
            },
          );
        },
      };
    },
    [gapiAuthorized, authorizedScopes, getOrCreateDataFile, putAppDataFile],
  );

  /**
   * Finish what a redirect sign-in started.
   *
   * Placement matters more than the contents. React runs effects in the order
   * they are declared, and `sp.current` is assigned by updateStorageProvider
   * directly above - so this has to sit after it, or configureStorageProvider
   * finds an unregistered provider and reports exactly that. The same trap the
   * comment inside updateStorageProvider describes, one level up.
   */
  useEffect(
    function replayRedirectIntent() {
      // gapiClientLoaded, not just gapiAuthorized. Coming back from the redirect
      // the token is read straight out of localStorage, so "authorised" is true
      // within a tick of mounting - while apis.google.com/js/api.js is still in
      // flight. Configuring Drive then immediately reads the library through
      // gapi.client.drive, and the reward for being early is
      // "Can't find variable: gapi".
      if (!gapiAuthorized || !gapiClientLoaded) {
        return;
      }

      const intent = googleAuth.consumeRedirectIntent();

      if (intent && intent.configureProvider) {
        ctxRef.current.configureStorageProvider(StorageProviderKey.GoogleDrive);
      }
    },
    [gapiAuthorized, gapiClientLoaded],
  );

  const playVideo = useCallback(
    function (
      callback?: () => void,
      onStateChange?: (ev: YT.OnStateChangeEvent) => void,
      onError?: (ev: YT.OnErrorEvent) => void,
      options?: PlayVideoOptions,
    ) {
      if (!iframeApiLoaded) {
        ctxRef.current.callSnackbar('Unable to play video, YouTube iFrame Api is not ready');
        throw new Error('Unable to play video, YouTube iFrame Api is not ready');
      }
      if (!playerEl.current) {
        ctxRef.current.callSnackbar('Unable to play video, iframe not found');
        throw new Error('Unable to play video, iframe not found');
      }

      // I don't know what it is that this constructor creates
      // but it bears no resemblance to playerEvt.target
      new YT.Player(playerEl.current, {
        events: {
          onReady: (playerEvt) => {
            ytPlayer.current = playerEvt.target;

            // A track restored from the last session is loaded, not started -
            // see the resume path in player.tsx. Where it should start is the
            // embed url's `start` parameter rather than a seekTo here:
            // seekTo on a player that has never played begins playback, which
            // is the one thing a restore must not do.
            if (!options || options.autoplay !== false) {
              playerEvt.target.playVideo();
            }

            if (callback) callback();
          },
          onStateChange,
          onError,
        },
      });
    },
    [iframeApiLoaded],
  );

  const ctxValue = useMemo(
    () => ({
      iframeApiLoaded,
      dataApiLoaded,
      gapiClientLoaded,
      gapiAuthorized,
      authorizedScopes,

      playerEl,
      ytPlayer,

      searchPaged,
      getUserPlaylistsPaged,
      getPlaylistItemsPaged,
      getUserDrive,
      getDriveFolder,
      getDriveFileMetadata,
      getFilesInFolderRecursive,
      getDriveFileUrl,
      getPlaylist,
      getDuration,
      insertPlaylistItem,
      deletePlaylistItem,
      canWriteYouTube,

      resetAppDataFolder,
      downloadAppDataFolder,

      playVideo,

      authorize,
      authorizeAdditionalScope,
      isAuthorizing,
      canReadDriveFiles,
      canAuthorizeInPlace,
      logout,
      authorizationExpired,
      disconnect,
      getAccount,
      googleUser,
    }),
    [
      canReadDriveFiles,
      canWriteYouTube,
      canAuthorizeInPlace,
      iframeApiLoaded,
      dataApiLoaded,
      gapiClientLoaded,
      gapiAuthorized,
      authorizedScopes,
      playerEl,
      ytPlayer,
      searchPaged,
      getUserPlaylistsPaged,
      getPlaylistItemsPaged,
      getUserDrive,
      getDriveFolder,
      getDriveFileMetadata,
      getFilesInFolderRecursive,
      getDriveFileUrl,
      getPlaylist,
      getDuration,
      insertPlaylistItem,
      deletePlaylistItem,

      resetAppDataFolder,
      downloadAppDataFolder,
      playVideo,
      authorize,
      authorizeAdditionalScope,
      isAuthorizing,
      logout,
      authorizationExpired,
      disconnect,
      getAccount,
      googleUser,
    ],
  );

  return <GoogleApiContext.Provider value={ctxValue}>{children}</GoogleApiContext.Provider>;
}
